import * as Sentry from '@sentry/react';
import type { Breadcrumb, ErrorEvent, RequestEventData } from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import { APP_CONST } from '../../constant';

type SentryScopeContext = {
  tenantID: Nullish<string>;
  userID: Nullish<string>;
};

type CaptureSentryExceptionOption = {
  code?: number;
  componentStack?: Nullish<string>;
  source: string;
};

type SentryApiBreadcrumb = {
  duration: number;
  method?: Nullish<string>;
  resultCode?: Nullish<string>;
  status?: Nullish<number>;
  statusCode?: Nullish<number>;
  trID?: Nullish<string>;
  url?: Nullish<string>;
};

type CaptureSentryApiFailureOption = SentryApiBreadcrumb & {
  source: 'api-response-error' | 'api-response-status-code';
};

type SentryReplayConfig = {
  canvasEnabled: boolean;
  enabled: boolean;
  onErrorSampleRate: number;
  sessionSampleRate: number;
};

type SentryTracesSampleRule = {
  matcher: RegExp;
  sampleRate: number;
};

type SentryIntegration =
  | ReturnType<typeof Sentry.reactRouterV7BrowserTracingIntegration>
  | ReturnType<typeof Sentry.replayIntegration>
  | ReturnType<typeof Sentry.replayCanvasIntegration>;

const FILTERED_VALUE = '[Filtered]';
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const BEARER_TOKEN_PATTERN = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const SENSITIVE_KEY_PATTERN =
  /(authorization|cookie|token|password|secret|credential|otp|email|phone|address|profile|name|session|api[-_]?key|access[-_]?key|refresh[-_]?key)/i;
const SAFE_ID_KEY_SET = new Set(['id', 'tenantID', 'userID']);
const DEFAULT_REPLAY_SESSION_SAMPLE_RATE = 0;
const DEFAULT_REPLAY_ON_ERROR_SAMPLE_RATE = 1.0;
const DEFAULT_TRACES_SAMPLE_RATE = 0.1;
// 오류가 나면 세션이 끝날 때까지 이어 녹화하고, 라벨링 화면은 URL 변경이 활동으로 잡혀 유휴
// 종료가 늦어지므로 길이로 끊습니다. 오류 전 버퍼(최대 60초)를 포함하므로 오류 뒤 약 4~5분이 남습니다.
const REPLAY_MAX_DURATION_MS = 5 * 60 * 1000;

// 예상된 비즈니스 상황(작업 잠금 1412, 토큰 만료 3401, 계약 만료 45423, 인증 흐름, 권한 거부)은 Sentry 수집에서 제외합니다
export const SENTRY_IGNORE_ERROR_LIST: (string | RegExp)[] = [
  /^\[(1412|3401|45423)\]/,
  'Access token is empty',
  'Please authenticate',
  APP_CONST.NON_ACCESSIBLE_ROUTE_ERROR_NAME,
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSensitiveKey = (key: string) =>
  !SAFE_ID_KEY_SET.has(key) && SENSITIVE_KEY_PATTERN.test(key);

const removeQueryString = (url: string) => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.search = '';
    parsedUrl.hash = '';

    return parsedUrl.toString();
  } catch {
    return url.split(/[?#]/)[0];
  }
};

const scrubString = (value: string) =>
  value
    .replace(EMAIL_PATTERN, FILTERED_VALUE)
    .replace(BEARER_TOKEN_PATTERN, `Bearer ${FILTERED_VALUE}`)
    .replace(JWT_PATTERN, FILTERED_VALUE);

const scrubValue = (value: unknown, depth = 0): unknown => {
  if (depth > 4) return '[Truncated]';
  if (typeof value === 'string') return scrubString(value);
  if (Array.isArray(value))
    return value.map(item => scrubValue(item, depth + 1));
  if (!isRecord(value)) return value;

  return Object.entries(value).reduce<Record<string, unknown>>(
    (result, [key, item]) => {
      if (isSensitiveKey(key)) return result;

      result[key] = scrubValue(item, depth + 1);

      return result;
    },
    {},
  );
};

const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (breadcrumb.category?.startsWith('console')) return null;
  if (breadcrumb.category?.startsWith('ui.')) return null;

  return {
    ...breadcrumb,
    data: scrubValue(breadcrumb.data) as Breadcrumb['data'],
    message:
      typeof breadcrumb.message === 'string'
        ? scrubString(breadcrumb.message)
        : breadcrumb.message,
  };
};

const scrubSentryEvent = (event: ErrorEvent): ErrorEvent => {
  const request = event.request
    ? {
        ...event.request,
        cookies: undefined,
        data: undefined,
        headers: scrubValue(
          event.request.headers,
        ) as RequestEventData['headers'],
        query_string: undefined,
        url: event.request.url
          ? removeQueryString(event.request.url)
          : undefined,
      }
    : undefined;

  return {
    ...event,
    breadcrumbs: event.breadcrumbs
      ?.map(scrubBreadcrumb)
      .filter((breadcrumb): breadcrumb is Breadcrumb => breadcrumb !== null),
    contexts: scrubValue(event.contexts) as ErrorEvent['contexts'],
    exception: event.exception
      ? {
          ...event.exception,
          values: event.exception.values?.map(value => ({
            ...value,
            value:
              typeof value.value === 'string'
                ? scrubString(value.value)
                : value.value,
          })),
        }
      : event.exception,
    extra: scrubValue(event.extra) as ErrorEvent['extra'],
    message:
      typeof event.message === 'string'
        ? scrubString(event.message)
        : event.message,
    request,
    user: event.user?.id
      ? {
          id: event.user.id,
          ip_address: null,
          tenantID: event.user.tenantID,
        }
      : undefined,
  };
};

const parseBooleanEnv = (
  value: Nullish<ImportMetaEnv[keyof ImportMetaEnv]>,
  defaultValue: boolean,
) => {
  if (value == null) return defaultValue;

  return value === '1';
};

const clampSampleRate = (value: number) => Math.min(Math.max(value, 0), 1);

const parseSampleRateEnv = (
  value: Nullish<ImportMetaEnv[keyof ImportMetaEnv]>,
  defaultValue: number,
) => {
  if (value == null || value === '') return defaultValue;

  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) return defaultValue;

  return clampSampleRate(parsedValue);
};

// `*` 만 와일드카드로 취급하고 나머지 문자는 그대로 비교합니다.
const createSampleRuleMatcher = (pattern: string) =>
  new RegExp(
    `^${pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replaceAll('\\*', '.*')}$`,
  );

// `<패턴>=<비율>` 을 `;` 로 구분한 문자열을 트랜잭션 이름 기준 샘플링 규칙으로 변환합니다.
const parseTracesSampleRuleEnv = (
  value: Nullish<ImportMetaEnv[keyof ImportMetaEnv]>,
): SentryTracesSampleRule[] => {
  if (typeof value !== 'string' || value === '') return [];

  return value.split(';').reduce<SentryTracesSampleRule[]>((rules, rule) => {
    const separatorIndex = rule.lastIndexOf('=');

    if (separatorIndex < 0) return rules;

    const pattern = rule.slice(0, separatorIndex).trim();
    const sampleRate = Number(rule.slice(separatorIndex + 1).trim());

    if (!pattern || !Number.isFinite(sampleRate)) return rules;

    rules.push({
      matcher: createSampleRuleMatcher(pattern),
      sampleRate: clampSampleRate(sampleRate),
    });

    return rules;
  }, []);
};

// Session Replay 는 production 빌드에서만 켜고 오류 세션만 기록합니다. 다른 모드를 막는 이유와
// 정상 세션 샘플률을 0 으로 두는 이유는 같은 프로젝트의 Sentry Team 플랜 쿼터이고, 캔버스
// 리플레이가 opt-in 인 이유는 WebGL 화면이 시각 데이터를 노출하고 렌더링 성능에 영향을 주기 때문입니다.
export const getSentryReplayConfig = (): SentryReplayConfig => ({
  canvasEnabled: parseBooleanEnv(
    import.meta.env.VITE_APP_SENTRY_REPLAY_CANVAS_ENABLED,
    false,
  ),
  enabled:
    import.meta.env.MODE === 'production' &&
    parseBooleanEnv(import.meta.env.VITE_APP_SENTRY_REPLAY_ENABLED, false),
  onErrorSampleRate: parseSampleRateEnv(
    import.meta.env.VITE_APP_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
    DEFAULT_REPLAY_ON_ERROR_SAMPLE_RATE,
  ),
  sessionSampleRate: parseSampleRateEnv(
    import.meta.env.VITE_APP_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
    DEFAULT_REPLAY_SESSION_SAMPLE_RATE,
  ),
});

const getSentryIntegrations = ({
  canvasEnabled,
  enabled,
}: SentryReplayConfig): SentryIntegration[] => {
  const integrations: SentryIntegration[] = [
    Sentry.reactRouterV7BrowserTracingIntegration({
      createRoutesFromChildren,
      matchRoutes,
      useEffect,
      useLocation,
      useNavigationType,
    }),
  ];

  if (!enabled) return integrations;

  integrations.push(
    Sentry.replayIntegration({
      blockAllMedia: true,
      maskAllText: true,
      maxReplayDuration: REPLAY_MAX_DURATION_MS,
    }),
  );

  if (canvasEnabled) {
    integrations.push(Sentry.replayCanvasIntegration());
  }

  return integrations;
};

// 라우트별 규칙이 있으면 그 값을, 없으면 기본 샘플링 비율을 사용합니다.
export const resolveTracesSampleRate = (name: string) => {
  const matchedRule = parseTracesSampleRuleEnv(
    import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RULE,
  ).find(({ matcher }) => matcher.test(name));

  if (matchedRule) return matchedRule.sampleRate;

  return parseSampleRateEnv(
    import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE,
    DEFAULT_TRACES_SAMPLE_RATE,
  );
};

export const isSentryEnabled = () =>
  !import.meta.env.DEV &&
  import.meta.env.VITE_APP_SENTRY_ENABLED === '1' &&
  Boolean(import.meta.env.VITE_APP_SENTRY_DSN);

export const initSentry = () => {
  if (!isSentryEnabled()) return;

  const replayConfig = getSentryReplayConfig();

  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    // 마케팅 분석용 이벤트(analytics)를 Sentry Logs 로 전송하기 위해 활성화합니다.
    enableLogs: true,
    environment: import.meta.env.MODE,
    ignoreErrors: SENTRY_IGNORE_ERROR_LIST,
    integrations: getSentryIntegrations(replayConfig),
    release: import.meta.env.VITE_APP_VERSION,
    replaysOnErrorSampleRate: replayConfig.enabled
      ? replayConfig.onErrorSampleRate
      : 0,
    replaysSessionSampleRate: replayConfig.enabled
      ? replayConfig.sessionSampleRate
      : 0,
    sendDefaultPii: false,
    tracePropagationTargets: ['localhost', /^https:\/\/[^/]*\.vueron\.ai/],
    tracesSampler: ({ inheritOrSampleWith, name }) =>
      inheritOrSampleWith(resolveTracesSampleRate(name)),
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubSentryEvent,
  });
};

export const syncSentryScope = ({ tenantID, userID }: SentryScopeContext) => {
  if (!isSentryEnabled()) return;

  const normalizedUserID =
    userID && userID !== '-1' && userID !== '-2' ? userID : null;

  Sentry.setTag('tenant_id', tenantID ?? 'none');
  Sentry.setUser(
    normalizedUserID
      ? {
          id: normalizedUserID,
          ip_address: null,
          tenantID: tenantID ?? undefined,
        }
      : null,
  );
};

export const addSentryApiBreadcrumb = ({
  duration,
  method,
  resultCode,
  status,
  statusCode,
  trID,
  url,
}: SentryApiBreadcrumb) => {
  if (!isSentryEnabled()) return;

  Sentry.addBreadcrumb({
    category: 'api',
    data: {
      duration,
      method,
      resultCode,
      status,
      statusCode,
      trID,
      url: url ? removeQueryString(url) : undefined,
    },
    level:
      (status != null && status >= 400) ||
      (statusCode != null && statusCode >= 400)
        ? 'error'
        : 'info',
    type: 'http',
  });
};

export const captureSentryApiFailure = ({
  duration,
  method,
  resultCode,
  source,
  status,
  statusCode,
  trID,
  url,
}: CaptureSentryApiFailureOption) => {
  if (!isSentryEnabled()) return;

  Sentry.withScope(scope => {
    scope.setTag('error_source', source);

    if (method) scope.setTag('api_method', method.toUpperCase());
    if (resultCode) scope.setTag('api_result_code', resultCode);
    if (status != null) scope.setTag('api_http_status', `${status}`);
    if (statusCode != null) scope.setTag('api_status_code', `${statusCode}`);
    if (trID) scope.setTag('api_tr_id', trID);

    scope.setContext('api', {
      duration,
      method,
      resultCode,
      status,
      statusCode,
      trID,
      url: url ? removeQueryString(url) : undefined,
    });
    scope.setContext('location', {
      pathname: window.location.pathname,
    });

    Sentry.captureException(
      new Error(
        `[API] ${
          statusCode != null
            ? `statusCode ${statusCode}`
            : `HTTP status ${status}`
        } response`,
      ),
    );
  });
};

export const captureSentryException = (
  error: unknown,
  { code, componentStack, source }: CaptureSentryExceptionOption,
) => {
  if (!isSentryEnabled()) return;

  Sentry.withScope(scope => {
    scope.setTag('error_source', source);

    if (code) scope.setTag('error_code', `${code}`);
    if (componentStack) {
      scope.setContext('react', {
        componentStack,
      });
    }

    scope.setContext('location', {
      pathname: window.location.pathname,
    });

    Sentry.captureException(error);
  });
};
